import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  finishActivityOccurrence,
  finishActivityOccurrenceOnBehalf,
  resetActivityOccurrence,
} from "@/api/Activities";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import {
  type IActivityOccurrence,
  isEmployeePerformed,
  activityOccurrenceStatusSchema,
  type IPatientPerformedCategory,
  type IEmployeePerformedCategory,
  type IUnfinishedActivityOccurrenceStatus,
} from "@medoma/models/activities";
import type { IScheduledShift } from "@medoma/models/shifts";
import { useLingui } from "@lingui/react/macro";
import { isScheduledForFuture } from "./markAsFinishedUtils";

export function isFinishableActivityOccurrence(
  activityOccurrence:
    | IActivityOccurrence
    | IFinishableOrRevertableActivityOccurrence,
): activityOccurrence is IFinishableActivityOccurrence {
  const { status } = activityOccurrence;

  // If the activity occurrence is already finished, it is not finishable
  if (status === activityOccurrenceStatusSchema.enum.finished) {
    return false;
  }

  // All other activity occurrences are finishable ("no assigness" check is handled later)
  return true;
}

export function isRevertableActivityOccurrence(
  activityOccurrence:
    | IActivityOccurrence
    | IFinishableOrRevertableActivityOccurrence,
): activityOccurrence is IRevertableActivityOccurrence {
  const { status } = activityOccurrence;

  // If the activity occurrence is not finished, it is not revertable
  if (status !== activityOccurrenceStatusSchema.enum.finished) {
    return false;
  }

  // All other activity occurrences are revertable
  return true;
}

// An expired occurrence inside a finished visit can be finished "on behalf" —
// the visit was completed but this sub-activity was missed.
export function isFinishableOnBehalfActivityOccurrence(
  activityOccurrence:
    | IActivityOccurrence
    | IFinishableOrRevertableActivityOccurrence,
  isPartOfFinishedVisit: boolean,
): boolean {
  return (
    isPartOfFinishedVisit &&
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.expired
  );
}

export type IFinishableActivityOccurrence = StrictOmit<
  IActivityOccurrence,
  "status"
> & {
  status: IUnfinishedActivityOccurrenceStatus;
} & (
    | {
        category: IEmployeePerformedCategory;
        assignees: IScheduledShift[];
      }
    | {
        category: IPatientPerformedCategory;
        // PatientTask and PatientMeasurementTask don't have assignees property at runtime
        assignees?: never;
      }
  );

export type IRevertableActivityOccurrence = StrictOmit<
  IActivityOccurrence,
  "status"
> & {
  status: typeof activityOccurrenceStatusSchema.enum.finished;
};

export type IFinishableOrRevertableActivityOccurrence =
  | IFinishableActivityOccurrence
  | IRevertableActivityOccurrence;

export const useMarkActivityOccurrenceAsFinished = () => {
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { alert, confirm } = useDialog();
  const { t } = useLingui();

  const {
    mutate: markActivityOccurrenceAsFinishedMutation,
    isPending: isMarkingActivityOccurrenceAsFinished,
  } = useMutation({
    mutationFn: ({ id, activityId }: IFinishableActivityOccurrence) =>
      finishActivityOccurrence(activityId, id),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att markera aktivitetstillfället som klart.`} ${deducedError(
          error,
        )}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  const {
    mutate: revertMarkActivityOccurrenceAsFinished,
    isPending: isRevertingMarkActivityOccurrenceAsFinished,
  } = useMutation({
    mutationFn: ({ activityId, id }: IRevertableActivityOccurrence) =>
      resetActivityOccurrence(activityId, id),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ångra utfört aktivitetstillfälle.`} ${deducedError(
          error,
        )}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  const {
    mutate: markActivityOccurrenceAsFinishedOnBehalf,
    isPending: isMarkingActivityOccurrenceAsFinishedOnBehalf,
  } = useMutation({
    mutationFn: ({ activityId, id }: IActivityOccurrence) =>
      finishActivityOccurrenceOnBehalf(activityId, id),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att markera aktiviteten som utförd.`} ${deducedError(
          error,
        )}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  const markActivityOccurrenceAsFinished = async (
    activityOccurrence: IFinishableActivityOccurrence,
  ) => {
    // Only an employee-performed activity can be missing the employee who
    // performed it; a patient task has no assignees to leave empty.
    if (
      isEmployeePerformed(activityOccurrence) &&
      activityOccurrence.assignees.length === 0
    ) {
      return alert({
        title: t`Aktiviteten har inte tilldelats någon`,
        message: t`Det gick inte att markera aktivitetstillfället som utfört. Aktiviteten har inte tilldelats någon. Lägg till den som utförde aktiviteten och försök igen.`,
      });
    }

    if (isScheduledForFuture(activityOccurrence.start)) {
      const confirmedFutureFinish = await confirm({
        title: t`Markera som utfört`,
        message: t`Aktivitetstillfället är planerat att utföras i framtiden. Är du säker på att du vill markera det som utfört?`,
      });
      if (!confirmedFutureFinish) {
        return;
      }
    }

    markActivityOccurrenceAsFinishedMutation(activityOccurrence);
  };

  return {
    markActivityOccurrenceAsFinished,
    revertMarkActivityOccurrenceAsFinished,
    markActivityOccurrenceAsFinishedOnBehalf,
    isMarkingActivityOccurrenceAsFinished,
    isRevertingMarkActivityOccurrenceAsFinished,
    isMarkingActivityOccurrenceAsFinishedOnBehalf,
  };
};
